(() => {
  if (window.SportsHubEmailEvents) return;

  // Local adapter only: the approved integration must explicitly supply the official provider.
  const controller = new AbortController();
  const queue = [];
  const viewedDrawers = new WeakSet();
  const pendingDrawerViews = new Map();
  const variants = new Map();
  const pendingVariants = new Map();
  const pendingAddConfirmations = new Set();
  let provider = null;
  let baseline = null;
  let identity = null;
  let timer = null;
  let epoch = 0;
  let pageViewed = false;
  let destroyed = false;
  let unsubscribe = null;
  let observer = null;
  let draining = null;
  let cancelIdentity = null;
  let identityFailed = false;
  let drawerViewEpoch = 0;
  let pendingPageView = null;
  let cartRevision = 0;

  const cancelPageView = () => {
    clearTimeout(pendingPageView?.timer);
    pendingPageView = null;
  };
  const cancelAddConfirmations = () => {
    for (const pending of pendingAddConfirmations) clearTimeout(pending.timer);
    pendingAddConfirmations.clear();
  };

  const cancelDrawerView = (drawer) => {
    clearTimeout(pendingDrawerViews.get(drawer)?.timer);
    pendingDrawerViews.delete(drawer);
  };
  const cancelDrawerViews = () => {
    drawerViewEpoch++;
    for (const drawer of pendingDrawerViews.keys()) cancelDrawerView(drawer);
  };

  const localeAliases = new Map([['es', 'es'], ['fr', 'fr'], ['de', 'de'], ['it', 'it'], ['nb', 'nb'], ['no', 'nb'], ['nl', 'nl'], ['pt', 'pt-PT'], ['pt-pt', 'pt-PT']]);
  const locale = () => {
    const value = window.Shopify?.locale || document.documentElement.lang || '';
    return typeof value === 'string' ? localeAliases.get(value.trim().replace(/_/g, '-').toLowerCase()) || null : null;
  };
  const visible = (element) => {
    if (!element?.isConnected || !element.getClientRects().length) return false;
    const box = element.getBoundingClientRect();
    if (box.width <= 0 || box.height <= 0 || box.bottom <= 0 || box.right <= 0 || box.top >= window.innerHeight || box.left >= window.innerWidth) return false;
    for (let node = element; node; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (node.hidden || node.getAttribute('aria-hidden') === 'true' || style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
    }
    return true;
  };
  const id = (value) => {
    if (typeof value === 'number' && !Number.isSafeInteger(value)) return null;
    return String(value || '').match(/^(?:gid:\/\/shopify\/(?:Product|ProductVariant)\/)?([1-9]\d{0,19})$/)?.[1] || null;
  };
  const path = () => {
    const url = new URL(window.location.href);
    return /^\/(?:[a-z]{2}(?:-[A-Za-z]{2})?\/)?(?:(?:products|collections)\/[a-z0-9-]+|cart)?\/?$/.test(url.pathname) ? url.origin + url.pathname : null;
  };
  const amount = (value) => typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value)) && Number(value) >= 0 ? Number(value) : null;
  const consent = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return !destroyed && window.Shopify?.designMode !== true && Boolean(locale()) && privacy?.analyticsProcessingAllowed?.() === true && privacy?.marketingAllowed?.() === true;
    } catch { return false; }
  };
  const reset = () => {
    clearTimeout(timer);
    timer = null;
    cancelDrawerViews();
    cancelPageView();
    cancelAddConfirmations();
    queue.length = 0;
    identity = null;
    // Cancelling our wait cannot cancel an identify already handed to the provider.
    // Keep this barrier across withdrawal and regrant until a new explicit signup identification.
    identityFailed ||= Boolean(cancelIdentity);
    cancelIdentity?.();
    draining = null;
    baseline = null;
    cartRevision++;
    epoch++;
    pendingVariants.clear();
    variants.clear();
  };
  const allowed = () => {
    if (consent()) return true;
    reset();
    return false;
  };
  const expire = () => {
    const now = Date.now();
    while (queue.length && now - queue[0].at >= 30000) queue.shift();
    if (identity && now - identity.at >= 5000) identity = null;
    clearTimeout(timer);
    const next = Math.min(queue[0]?.at + 30000 || Infinity, identity?.at + 5000 || Infinity);
    timer = Number.isFinite(next) ? setTimeout(expire, Math.max(1, next - now)) : null;
  };
  const waitForIdentity = (result) => new Promise((resolve) => {
    let finished = false;
    const finish = (ok) => {
      if (finished) return;
      finished = true;
      clearTimeout(timeout);
      if (cancelIdentity === cancel) cancelIdentity = null;
      resolve(ok);
    };
    const cancel = () => finish(false);
    const timeout = setTimeout(cancel, 5000);
    cancelIdentity = cancel;
    if (typeof result?.then !== 'function') finish(false);
    else Promise.resolve(result).then((value) => finish(value !== false), cancel);
  });
  const flush = async () => {
    if (draining || !allowed()) return;
    expire();
    if (!provider) return;
    const active = provider;
    const generation = epoch;
    const ticket = {};
    draining = ticket;
    try {
      while (generation === epoch && active === provider && allowed()) {
        expire();
        if (identity) {
          const pending = identity;
          identity = null;
          let identified = false;
          try {
            const identify = active.identify;
            if (typeof identify === 'function' && allowed()) {
              const response = identify.call(active, { email: pending.email });
              pending.email = '';
              identified = await waitForIdentity(response);
            }
          } catch { /* Failed identification must never attribute the queue to the previous profile. */ }
          finally { pending.email = ''; }
          if (generation !== epoch || active !== provider || !allowed()) return;
          if (!identified) { identityFailed = true; queue.length = 0; break; }
          continue;
        }
        if (identityFailed || !queue.length) break;
        const event = queue.shift();
        try {
          const track = active.track;
          if (typeof track === 'function' && allowed()) Promise.resolve(track.call(active, event.name, event.properties)).catch(() => {});
        } catch { /* Do not replay failed provider calls or interfere with shopping. */ }
      }
    } finally {
      if (draining === ticket) draining = null;
      expire();
    }
  };
  const eventId = () => {
    try { return window.crypto.randomUUID(); }
    catch { return null; }
  };
  const track = (name, properties) => {
    if (!allowed() || document.hidden || identityFailed) return;
    const uuid = eventId();
    if (!uuid) return;
    if (queue.length === 20) queue.shift();
    const page = path();
    queue.push({ name, properties: { ...properties, schema_version: 1, $event_id: uuid, occurred_at: new Date(Date.now()).toISOString(), locale: locale(), ...(page ? { page_url: page } : {}) }, at: Date.now() });
    flush();
  };

  // Keep only catalog IDs and quantities, never a raw cart, token, notes, attributes or properties.
  const snapshot = (cart) => {
    if (!cart || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0 ||
        !/^[A-Z]{3}$/.test(cart.currency || '') || !Number.isFinite(cart.total_price) || cart.total_price < 0 || cart.status >= 400 || cart.errors) return null;
    const items = new Map();
    const catalog = [];
    let count = 0;
    for (const item of cart.items) {
      if (!item || typeof item !== 'object') return null;
      const variant = id(item.variant_id || item.id);
      if (!variant || !Number.isSafeInteger(item.quantity) || item.quantity < 0) return null;
      count += item.quantity;
      // Exact rule from sh-cart.giftItems(); giftProperties() sets it for both supported providers.
      const gift = item.properties?._sh_reward === 'gift-v1';
      const sku = typeof item.sku === 'string' && item.sku.length <= 80 && !/[@<>\u0000-\u001f]/.test(item.sku) ? item.sku : null;
      const entry = { variant_id: variant, ...(id(item.product_id) ? { product_id: id(item.product_id) } : {}), quantity: item.quantity,
        ...(sku ? { sku } : {}), ...(Number.isFinite(item.final_price) && item.final_price >= 0 ? { unit_price: item.final_price / 100 } : {}), is_gift: gift };
      catalog.push(entry);
      if (gift) continue;
      const previous = items.get(variant);
      items.set(variant, { ...entry, quantity: (previous?.quantity || 0) + item.quantity });
    }
    return count === cart.item_count ? { items, catalog, item_count: count, currency: cart.currency, cart_total: cart.total_price / 100,
      ...(Number.isFinite(cart.items_subtotal_price) && cart.items_subtotal_price >= 0 ? { cart_subtotal: cart.items_subtotal_price / 100 } : {}) } : null;
  };
  const readCart = (element) => {
    for (const cart of element ? [element] : document.querySelectorAll('sh-cart')) {
      const current = snapshot(cart.cart);
      if (current) return current;
      // A deferred collector can run before sh-cart upgrades or before its init() follows the view event.
      // Parse our rendered config transiently and retain only the same explicit snapshot allowlist.
      try {
        const raw = cart.querySelector?.('[data-cart-config]')?.textContent;
        const rendered = raw ? snapshot(JSON.parse(raw)?.cart) : null;
        if (rendered) return rendered;
      } catch { /* Missing or malformed config never blocks the native cart. */ }
    }
    return null;
  };
  const seedCart = () => {
    const page = /\/cart\/?$/.test(window.location.pathname);
    const element = page ? document.querySelector('main[data-template="cart"] sh-cart[data-mode="page"]') : null;
    baseline = page ? element ? readCart(element) : null : readCart();
    cartRevision++;
  };
  const matchesCart = (current, cart) => {
    const total = amount(cart?.cost?.totalAmount?.amount);
    return current && total !== null && current.item_count === cart?.totalQuantity && current.currency === cart?.cost?.totalAmount?.currencyCode && Math.abs(current.cart_total - total) < 0.001;
  };
  const pageCartViewed = () => {
    if (pageViewed || pendingPageView || !allowed() || document.hidden || !/\/cart\/?$/.test(window.location.pathname)) return;
    const pending = { until: Date.now() + 1000, timer: null };
    pendingPageView = pending;
    const check = () => {
      if (pendingPageView !== pending) return;
      if (pageViewed || !allowed() || document.hidden || !/\/cart\/?$/.test(window.location.pathname) || Date.now() > pending.until) {
        cancelPageView(); return;
      }
      const element = document.querySelector('main[data-template="cart"] sh-cart[data-mode="page"]');
      const current = visible(element) ? readCart(element) : null;
      if (current) {
        pageViewed = true;
        cancelPageView();
        observeCart(current);
        track('SH Cart Viewed', { surface: 'page', item_count: current.item_count, cart_total: current.cart_total, currency: current.currency,
          product_ids: [...new Set(current.catalog.map((item) => item.product_id).filter(Boolean))],
          variant_ids: [...new Set(current.catalog.map((item) => item.variant_id))], Items: current.catalog,
          ...(current.cart_subtotal !== undefined ? { cart_subtotal: current.cart_subtotal } : {}) });
      } else if (Date.now() < pending.until) pending.timer = setTimeout(check, 50);
      else cancelPageView();
    };
    check();
  };
  const observeCart = (current, onlyAdded = false) => {
    if (!current) return;
    const previous = baseline;
    baseline = current;
    cartRevision++;
    if (!previous || previous.currency !== current.currency) return;
    for (const [variant, item] of onlyAdded ? [] : previous.items) {
      const remaining = current.items.get(variant)?.quantity || 0;
      if (item.quantity <= remaining) continue;
      track('SH Removed From Cart', {
        variant_id: variant, ...(item.product_id ? { product_id: item.product_id } : {}),
        ...(item.sku ? { sku: item.sku } : {}), quantity_before: item.quantity, quantity_after: remaining,
        quantity_removed: item.quantity - remaining, currency: current.currency,
        ...(current.cart_subtotal !== undefined ? { cart_subtotal: current.cart_subtotal } : {}),
      });
    }
    for (const [variant, item] of current.items) {
      const before = previous.items.get(variant)?.quantity || 0;
      if (item.quantity <= before) continue;
      const pricedLines = current.catalog.filter((line) => line.variant_id === variant && !line.is_gift);
      const unitPrice = Number.isFinite(item.unit_price) && pricedLines.every((line) => line.unit_price === item.unit_price) ? item.unit_price : null;
      track('SH Added To Cart', {
        variant_id: variant, ...(item.product_id ? { product_id: item.product_id } : {}),
        ...(item.sku ? { sku: item.sku } : {}), quantity_before: before, quantity_after: item.quantity,
        quantity_added: item.quantity - before, ...(unitPrice !== null ? { unit_price: unitPrice } : {}), currency: current.currency,
        ...(current.cart_subtotal !== undefined ? { cart_subtotal: current.cart_subtotal } : {}),
      });
    }
  };
  const cartUpdated = (event) => {
    if (!allowed()) return;
    observeCart(snapshot(event?.cartData));
    if (window.SportsHubEmailContext?.observeCart(event?.cartData, { mutation: true, partial: true }) === false) window.SportsHubEmailContext?.refreshCart({ mutation: true });
  };
  const cartLinesUpdated = (event) => {
    if (!allowed() || document.hidden || event.action !== 'add' || typeof event.promise?.then !== 'function') return;
    const promise = event.promise;
    event = null;
    if (pendingAddConfirmations.size === 20) {
      const oldest = pendingAddConfirmations.values().next().value;
      clearTimeout(oldest.timer); pendingAddConfirmations.delete(oldest);
    }
    const pending = { revision: cartRevision, at: Date.now(), timer: null };
    pendingAddConfirmations.add(pending);
    const finish = () => { clearTimeout(pending.timer); pendingAddConfirmations.delete(pending); };
    const active = () => pendingAddConfirmations.has(pending) && pending.revision === cartRevision && allowed() && !document.hidden;
    pending.timer = setTimeout(finish, 5000);
    Promise.resolve(promise).then((result) => {
      clearTimeout(pending.timer); pending.timer = null;
      if (!active() || Date.now() - pending.at > 5000 || !result || (result.userErrors !== undefined && (!Array.isArray(result.userErrors) || result.userErrors.length))) { finish(); return; }
      const total = amount(result.cart?.cost?.totalAmount?.amount);
      const currency = result.cart?.cost?.totalAmount?.currencyCode;
      const count = result.cart?.totalQuantity;
      if (total === null || !/^[A-Z]{3}$/.test(currency || '') || !Number.isSafeInteger(count) || count < 0) { finish(); return; }
      // Retain only public totals, never the result cart or intended input lines.
      const confirmed = { totalQuantity: count, cost: { totalAmount: { amount: String(total), currencyCode: currency } } };
      result = null;
      const until = Date.now() + 1000;
      const check = () => {
        if (!active() || Date.now() > until) { finish(); return; }
        const candidates = [];
        for (const element of document.querySelectorAll('sh-cart')) {
          const current = readCart(element);
          if (matchesCart(current, confirmed)) candidates.push(current);
        }
        if (candidates.length === 1) { finish(); observeCart(candidates[0], true); window.SportsHubEmailContext?.refreshCart({ mutation: true }); }
        else if (Date.now() < until) pending.timer = setTimeout(check, 50);
        else finish();
      };
      check();
    }).catch(finish);
  };
  const recordDrawerView = (drawer, properties, cart) => {
    const pending = { properties, cart, cartRevision, generation: drawerViewEpoch, until: Date.now() + 1000, timer: null };
    pendingDrawerViews.set(drawer, pending);
    const check = () => {
      if (pendingDrawerViews.get(drawer) !== pending) return;
      if (pending.generation !== drawerViewEpoch || !allowed() || document.hidden || !drawer.isConnected ||
          !drawer.classList.contains('active') || drawer.classList.contains('is-notice') || drawer.classList.contains('is-closing')) {
        cancelDrawerView(drawer); return;
      }
      if (Date.now() > pending.until) { cancelDrawerView(drawer); return; }
      if (visible(drawer.querySelector('.drawer__inner'))) {
        cancelDrawerView(drawer);
        viewedDrawers.add(drawer);
        // A newer full observation already accounts for this change; never rewind its baseline.
        if (pending.cartRevision === cartRevision) observeCart(pending.cart);
        track('SH Cart Viewed', pending.properties);
      } else if (Date.now() < pending.until) pending.timer = setTimeout(check, 50);
      else cancelDrawerView(drawer);
    };
    check();
  };
  const cartViewed = (event) => {
    if (!allowed() || document.hidden) return;
    let surface, drawer, pageElement;
    if (event.context === 'dialog') {
      drawer = event.target?.closest?.('cart-drawer');
      if (!drawer || !drawer.classList.contains('active') || drawer.classList.contains('is-notice') || drawer.classList.contains('is-closing') ||
          viewedDrawers.has(drawer) || pendingDrawerViews.has(drawer)) return;
      surface = 'drawer';
    } else if (event.context === 'page') {
      pageElement = document.querySelector('main[data-template="cart"] sh-cart[data-mode="page"]');
      if (pageViewed || !/\/cart\/?$/.test(window.location.pathname) || !visible(pageElement)) return;
      pageViewed = true;
      cancelPageView();
      surface = 'page';
    } else return;
    const cart = event.cart;
    const total = amount(cart?.cost?.totalAmount?.amount);
    const currency = cart?.cost?.totalAmount?.currencyCode;
    // StandardEventCart has no product/variant fields. Enrich only from a matching current AJAX cart.
    const element = drawer ? drawer.querySelector('sh-cart') : pageElement;
    const current = element ? readCart(element) : null;
    const matching = matchesCart(current, cart) ? current : null;
    const properties = {
      surface,
      ...(cart === null ? { item_count: 0 } : Number.isSafeInteger(cart?.totalQuantity) && cart.totalQuantity >= 0 ? { item_count: cart.totalQuantity } : {}),
      ...(cart && total !== null && /^[A-Z]{3}$/.test(currency || '') ? { cart_total: total, currency } : {}),
      ...(matching ? { product_ids: [...new Set(matching.catalog.map((item) => item.product_id).filter(Boolean))],
        variant_ids: [...new Set(matching.catalog.map((item) => item.variant_id))], Items: matching.catalog,
        ...(matching.cart_subtotal !== undefined ? { cart_subtotal: matching.cart_subtotal } : {}) } : {}),
    };
    if (drawer) recordDrawerView(drawer, properties, matching);
    else { observeCart(matching); track('SH Cart Viewed', properties); }
  };
  const catalogOptions = (root, product, variant) => {
    try {
      const pickers = root?.matches?.('variant-selects') ? [root] : root?.querySelectorAll?.('variant-selects') || [];
      let currentPicker = null;
      for (const picker of pickers) {
        if (!visible(picker) || id(picker.dataset?.productId) !== product) continue;
        const current = JSON.parse(picker.querySelector('[data-selected-variant]')?.textContent || 'null');
        if (id(current?.id) !== variant) continue;
        if (currentPicker) return [];
        currentPicker = picker;
      }
      const options = [];
      for (const select of currentPicker?.querySelectorAll('select') || []) {
        if (select.closest('variant-selects') !== currentPicker || select.selectedOptions.length !== 1) continue;
        const option = select.selectedOptions[0];
        if (option.closest('select') === select) options.push({ name: option.dataset?.optionName, value: option.value });
      }
      return options;
    } catch { /* Missing or stale catalog markup never supplies inferred option values. */ }
    return [];
  };
  const variantSelected = (event) => {
    if (!allowed() || document.hidden || !id(event.product?.id) || typeof event.promise?.then !== 'function') return;
    const product = id(event.product.id);
    const picker = event.target?.closest?.('variant-selects');
    // product-info replaces the picker after resolving the promise; read its current visible catalog only.
    const pickerRoot = picker?.closest?.('product-info') || picker;
    const generation = (pendingVariants.get(product) || 0) + 1;
    const started = epoch;
    pendingVariants.set(product, generation);
    Promise.resolve(event.promise).then(({ variant } = {}) => {
      if (started !== epoch || pendingVariants.get(product) !== generation || !allowed() || document.hidden || !id(variant?.id) || variants.get(product) === id(variant.id)) return;
      const options = {};
      const selected = [];
      const namedOptions = Array.isArray(variant.selectedOptions) && variant.selectedOptions.length ? variant.selectedOptions : catalogOptions(pickerRoot, product, id(variant.id));
      for (const option of namedOptions) {
        const name = String(option.name || '').normalize('NFC').trim().toLowerCase().replace(/\s*:\s*$/, '');
        const value = typeof option.value === 'string' ? option.value.trim() : '';
        if (!value || value.length > 80 || /[@<>\u0000-\u001f]/.test(value)) continue;
        if (['talla', 'size', 'tamaño', 'taille', 'größe', 'groesse', 'taglia', 'størrelse', 'storrelse', 'maat', 'tamanho'].includes(name)) { options.size = value; selected.push({ name: 'size', value }); }
        if (['color', 'colour', 'couleur', 'farbe', 'colore', 'farge', 'kleur', 'cor'].includes(name)) { options.color = value; selected.push({ name: 'color', value }); }
      }
      const previous = variants.get(product);
      variants.set(product, id(variant.id));
      const price = amount(variant.price?.amount);
      const currency = variant.price?.currencyCode;
      track('SH Variant Selected', { product_id: product, variant_id: id(variant.id), ...options, selected_options: selected,
        ...(previous ? { previous_variant_id: previous } : {}),
        ...(variant.price && price !== null && /^[A-Z]{3}$/.test(currency || '') ? { price, currency } : {}),
        ...(typeof variant.availableForSale === 'boolean' ? { available: variant.availableForSale } : {}) });
    }).catch(() => {});
  };

  const start = () => {
    if (!locale() || destroyed || window.Shopify?.designMode === true) return;
    const options = { signal: controller.signal };
    if (allowed()) seedCart();
    document.addEventListener('shopify:cart:view', cartViewed, options);
    document.addEventListener('shopify:cart:lines-update', cartLinesUpdated, options);
    document.addEventListener('shopify:product:select', variantSelected, options);
    document.addEventListener('visitorConsentCollected', () => {
      reset();
      if (consent()) seedCart();
      pageCartViewed();
    }, options);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { cancelDrawerViews(); cancelPageView(); cancelAddConfirmations(); }
      else pageCartViewed();
    }, options);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, cartUpdated);
    observer = new MutationObserver((records) => {
      for (const record of records) if (!record.target.classList.contains('active') || record.target.classList.contains('is-closing') || record.target.classList.contains('is-notice')) {
        viewedDrawers.delete(record.target);
        cancelDrawerView(record.target);
      }
    });
    for (const drawer of document.querySelectorAll('cart-drawer')) observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    pageCartViewed();
  };

  const queueIdentity = (email) => {
    if (!allowed() || typeof email !== 'string' || !/^[^\s@]+@[^\s@]+$/.test(email.trim())) return false;
    identityFailed = false;
    identity = { email: email.trim(), at: Date.now() };
    flush();
    return true;
  };
  window.SportsHubEmailEvents = Object.freeze({
    setProvider(next) {
      if (cancelIdentity) { identityFailed = true; queue.length = 0; }
      cancelIdentity?.();
      epoch++;
      draining = null;
      try { provider = typeof next?.track === 'function' && typeof next?.identify === 'function' ? next : null; }
      catch { provider = null; }
      if (provider && !baseline && consent()) seedCart();
      if (provider) pageCartViewed();
      flush();
    },
    // Call only from the existing confirmed native success path, never from an input or submit attempt.
    identifyAfterSignup(email) {
      if (allowed()) window.SportsHubEmailContext?.resetIdentity();
      return queueIdentity(email);
    },
    // Accept only the exact email supplied by the authenticated server-rendered customer payload.
    identifyAuthenticatedCustomer(email) {
      if (typeof email !== 'string' || email !== email.trim()) return false;
      return queueIdentity(email);
    },
    destroy() {
      destroyed = true;
      reset();
      controller.abort();
      unsubscribe?.();
      observer?.disconnect();
      provider = null;
    },
  });
  window.addEventListener('pagehide', reset, { signal: controller.signal });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true, signal: controller.signal });
  else start();
})();

